// Portfolio: what is going on across the estate. Three headline numbers,
// then every agreement as a tree by category or by
// vendor, each node tallying what its columns show, and a
// third tab for the deals whose usage is not connected. The filters belong
// to the table's Filter menu; the view applies them to the deals and
// rebuilds the tree, so the tallies stay true to the rows shown. Only
// Microsoft and its EA drill down.

import { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Button,
    DataTable,
    DataTableActions,
    DataTableActiveFilters,
    DataTableColumn,
    DataTableEntityCell,
    Tabs,
    Tooltip,
    message,
} from "../../../../design-system";
import { VendorPage } from "../../../vendor/VendorPage";
import { Metrics } from "../../../vendor/list/components/Metrics";
import {
    CATEGORIES,
    DEAL_STATUS_LABEL,
    DealStatus,
    GroupBy,
    PortfolioNode,
    contractValue,
    dealStatus,
    effectiveProjects,
    portfolioTotals,
    portfolioUtilization,
    portfolioTree,
    projectForDeal,
    tallyUtilization,
    utilizationOf,
    utilizationTier,
    vendorOfDeal,
} from "../model/derived";
import { DEALS, DRILLABLE_DEAL_IDS, SPEND_VS_LAST_YEAR, UTILIZATION_BENCHMARK, personName } from "../model/fixtures";
import { daysUntil, longDate, money, pct, relativeDays } from "../model/format";
import { requestUsageAnalysis, useUsageDemoState } from "../model/store";
import type { UsageDeal, UsageProject } from "../model/types";
import { usageDealPath } from "../paths";
import {
    DealStatusCell,
    NameCell,
    NotInDemo,
    UsageMeasureCell,
    UtilizationBar,
    vendorFallback,
} from "../components/cells";
import classes from "./usage.module.less";

type Tab = "category" | "vendor" | "not_connected";

const NOTICE_WINDOWS = [
    { value: "30", label: "Notice in 30 days" },
    { value: "90", label: "Notice in 90 days" },
    { value: "180", label: "Notice in 180 days" },
];

const noticeWithin = (deal: UsageDeal, days: string) => {
    const left = daysUntil(deal.term.noticeBy);
    return left >= 0 && left <= Number(days);
};

/** Whether the deal passes the active filters: category, owner, status, notice window. */
const passes = (deal: UsageDeal, filters: DataTableActiveFilters, projects: UsageProject[]) => {
    const { category, owner, status, notice } = filters;
    if (category?.length && !category.includes(vendorOfDeal(deal).category)) return false;
    if (owner?.length && !owner.includes(deal.owner)) return false;
    if (status?.length && !status.includes(dealStatus(deal, projects))) return false;
    if (notice?.length && !noticeWithin(deal, notice[0])) return false;
    return true;
};

/** A notice date as days left, the date itself on hover; a dash once past or absent. */
const NoticeDays: FC<{ iso?: string }> = ({ iso }) =>
    iso ? (
        <Tooltip title={`Notice by ${longDate(iso)}`}>
            <span>{relativeDays(iso).replace("in ", "")}</span>
        </Tooltip>
    ) : (
        <>–</>
    );

export const PortfolioView: FC = () => {
    const state = useUsageDemoState();
    const projects = useMemo(() => effectiveProjects(state), [state]);
    const [tab, setTab] = useState<Tab>("vendor");
    const [filters, setFilters] = useState<DataTableActiveFilters>({});

    const totals = portfolioTotals();
    const coverage = (totals.connectedSpend / totals.spend) * 100;
    // Coverage scores more kindly than a utilization: three quarters connected is good.
    const coverageTier = (percent: number) => (percent < 50 ? "red" : percent < 75 ? "orange" : "green");
    const utilization = portfolioUtilization(DEALS);
    const owners = useMemo(() => Array.from(new Set(DEALS.map((d) => d.owner))).sort(), []);

    const deals = DEALS.filter((d) => passes(d, filters, projects));
    const notConnected = deals.filter((d) => d.usageStatus === "not_connected");
    const tree = useMemo(() => (tab === "not_connected" ? [] : portfolioTree(tab as GroupBy, deals)), [tab, deals]);

    const request = (dealId: string, name: string) => {
        requestUsageAnalysis(dealId);
        message.success(`Usage analysis requested for ${name}. It is waiting on InfinityLoop in the Pipeline.`);
    };

    // ---- The filters, hosted by the columns they read --------------------
    const categoryFilter = {
        filterLabel: "Category",
        filter: {
            type: "enum" as const,
            options: CATEGORIES.map((c) => ({ value: c, label: c })),
            value: () => undefined as string | undefined,
        },
    };
    const ownerFilter = {
        type: "enum" as const,
        options: owners.map((o) => ({ value: o, label: personName(o) })),
        value: () => undefined as string | undefined,
    };
    const noticeFilter = {
        type: "pick" as const,
        render: (value: string | undefined, onChange: (value: string | undefined) => void) => (
            <div className={classes.pickList}>
                {NOTICE_WINDOWS.map((w) => (
                    <button
                        key={w.value}
                        type="button"
                        className={classes.pickList__item}
                        aria-pressed={value === w.value}
                        onClick={() => onChange(value === w.value ? undefined : w.value)}
                    >
                        {w.label}
                    </button>
                ))}
            </div>
        ),
        label: (value: string) => NOTICE_WINDOWS.find((w) => w.value === value)?.label ?? value,
        match: () => true,
    };
    const statusFilter = {
        type: "enum" as const,
        options: (Object.keys(DEAL_STATUS_LABEL) as DealStatus[]).map((s) => ({ value: s, label: DEAL_STATUS_LABEL[s] })),
        value: () => undefined as string | undefined,
    };

    // ---- The tree: a group or vendor tallies what its deals show ---------
    const treeColumns: DataTableColumn<PortfolioNode>[] = [
        {
            key: "category",
            title: "Name",
            minWidth: 340,
            sort: "text",
            sortValue: (node) =>
                node.kind === "group" ? node.label : node.kind === "vendor" ? node.vendor.name : node.deal.name,
            ...categoryFilter,
            render: (node) => {
                if (node.kind === "group") return <NameCell name={node.label} />;
                if (node.kind === "vendor") {
                    const { letter, color } = vendorFallback(node.vendor);
                    return (
                        <DataTableEntityCell
                            size="small"
                            nameSize="large"
                            imageSrc={node.vendor.hero}
                            imageFallback={letter}
                            imageFallbackColor={color}
                            name={node.vendor.name}
                        />
                    );
                }
                const drillable = DRILLABLE_DEAL_IDS.includes(node.deal.id);
                return (
                    <NameCell
                        indent
                        weight="plain"
                        name={
                            drillable ? (
                                <Link to={usageDealPath(node.deal.id)}>{node.deal.name}</Link>
                            ) : (
                                <NotInDemo>{node.deal.name}</NotInDemo>
                            )
                        }
                    />
                );
            },
        },
        {
            key: "status",
            title: "Status",
            filter: statusFilter,
            render: (node) => {
                if (node.kind !== "deal") return null;
                return <DealStatusCell status={dealStatus(node.deal, projects)} project={projectForDeal(node.deal.id, projects)} />;
            },
        },
        {
            key: "usage",
            title: "Utilization",
            sort: "number",
            sortValue: (node) => (node.kind === "deal" ? utilizationOf(node.deal) : tallyUtilization(node)),
            render: (node) => {
                if (node.kind === "deal") return <UsageMeasureCell deal={node.deal} stacked />;
                const utilization = tallyUtilization(node);
                return utilization != null ? <UtilizationBar percent={utilization} stacked /> : null;
            },
        },
        {
            key: "overage",
            title: "Overage",
            align: "right",
            sort: "number",
            sortValue: (node) => node.overage || undefined,
            render: (node) => (node.overage > 0 ? <span className={classes.overdue}>{money(node.overage)}</span> : null),
        },
        {
            key: "atStake",
            title: "At stake",
            align: "right",
            sort: "number",
            sortValue: (node) => node.atStake || undefined,
            render: (node) => (node.atStake > 0 ? money(node.atStake) : "–"),
        },
        {
            key: "tcv",
            title: "TCV",
            align: "right",
            sort: "number",
            sortValue: (node) => node.contractValue,
            render: (node) => money(node.contractValue),
        },
        {
            key: "notice",
            title: "Next notice",
            align: "right",
            sort: "date",
            sortValue: (node) => node.nextNotice,
            filter: noticeFilter,
            render: (node) => <NoticeDays iso={node.nextNotice} />,
        },
    ];

    // ---- The deals without usage: a flat list, each offering an analysis ----
    const flatColumns: DataTableColumn<UsageDeal>[] = [
        {
            key: "category",
            title: "Name",
            minWidth: 340,
            sort: "text",
            sortValue: (deal) => deal.name,
            ...categoryFilter,
            render: (deal) => {
                const vendor = vendorOfDeal(deal);
                const { letter, color } = vendorFallback(vendor);
                return (
                    <DataTableEntityCell
                        size="small"
                        imageSrc={vendor.hero}
                        imageFallback={letter}
                        imageFallbackColor={color}
                        name={<NotInDemo>{deal.name}</NotInDemo>}
                        detail={vendor.name}
                    />
                );
            },
        },
        {
            key: "owner",
            title: "Owner",
            filter: ownerFilter,
            render: (deal) => personName(deal.owner),
        },
        {
            key: "cost",
            title: "Annual cost",
            align: "right",
            sort: "number",
            sortValue: (deal) => deal.annualCost,
            render: (deal) => money(deal.annualCost),
        },
        {
            key: "tcv",
            title: "TCV",
            align: "right",
            sort: "number",
            sortValue: (deal) => contractValue(deal),
            render: (deal) => money(contractValue(deal)),
        },
        {
            key: "notice",
            title: "Next notice",
            align: "right",
            sort: "date",
            sortValue: (deal) => deal.term.noticeBy,
            filter: noticeFilter,
            render: (deal) => <NoticeDays iso={daysUntil(deal.term.noticeBy) >= 0 ? deal.term.noticeBy : undefined} />,
        },
        {
            key: "actions",
            title: "",
            actions: true,
            render: (deal) => {
                const requested = state.requestedDeals.includes(deal.id);
                return (
                    <DataTableActions>
                        <Button
                            size="small"
                            disabled={requested}
                            onClick={() => request(deal.id, `${vendorOfDeal(deal).name} ${deal.name}`)}
                        >
                            {requested ? "Requested" : "Request"}
                        </Button>
                    </DataTableActions>
                );
            },
        },
    ];

    const columnFilters = { value: filters, onChange: setFilters };

    const treeTable = (
        <DataTable<PortfolioNode>
            key={tab}
            columns={treeColumns}
            rows={tree}
            rowKey={(node) => node.key}
            childRows={(node) => (node.kind === "deal" ? undefined : node.children)}
            defaultExpanded={1}
            columnFilters={columnFilters}
            searchText={(node) =>
                node.kind === "group" ? node.label : node.kind === "vendor" ? node.vendor.name : [node.deal.name, node.vendor.name]
            }
            searchPlaceholder="Search vendors and deals"
            emptyText="Nothing matches these filters."
            dense
        />
    );

    return (
        <VendorPage>
            <Metrics
                metrics={[
                    {
                        label: "Value at stake",
                        value: money(totals.atStake),
                        color: "red",
                        detail: `${money(totals.unused)} unused, ${money(totals.overage.amount)} overage ahead; ${pct((totals.atStake / totals.connectedSpend) * 100)} of connected spend`,
                    },
                    {
                        label: "Utilization avg.",
                        value: utilization != null ? pct(utilization) : "–",
                        color: utilization != null ? utilizationTier(utilization) : undefined,
                        detail: `market average ${UTILIZATION_BENCHMARK.market}%, top quartile above ${UTILIZATION_BENCHMARK.topQuartile}%`,
                    },
                    {
                        label: "Spend with usage connected",
                        value: money(totals.connectedSpend),
                        secondary: money(totals.spend),
                        color: coverageTier(coverage),
                        detail: `${pct(coverage)} connected; +${SPEND_VS_LAST_YEAR.connected}% vs last year`,
                    },
                ]}
            />
            <Tabs
                variant="line"
                size="small"
                tabBarStyle={{ marginBottom: 8 }}
                activeKey={tab}
                onChange={(key) => setTab(key as Tab)}
                items={[
                    { key: "vendor", label: "By vendor", children: treeTable },
                    { key: "category", label: "By category", children: treeTable },
                    {
                        key: "not_connected",
                        label: "Not connected",
                        children: (
                            <DataTable<UsageDeal>
                                columns={flatColumns}
                                rows={notConnected}
                                rowKey={(deal) => deal.id}
                                columnFilters={columnFilters}
                                defaultSort={{ key: "cost", direction: "descending" }}
                                searchText={(deal) => [deal.name, vendorOfDeal(deal).name]}
                                searchPlaceholder="Search vendors and deals"
                                emptyText="Every deal has its usage connected."
                                dense
                            />
                        ),
                    },
                ]}
            />
        </VendorPage>
    );
};
